import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain,
  BookOpen,
  BarChart3,
  Smartphone,
  Clock,
  ArrowRight,
  Lock,
  Trophy
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const hasActiveSubscription = subscription?.hasSubscription;
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubjectClick = (category) => {
    if (!user) {
      navigate('/register');
      return;
    }
    // Simulace a Flashcards vyžadují předplatné - přesměrovat na stránku kde uvidí paywall
    if (category === 'simulation') {
      navigate('/simulation');
    } else if (category === 'flashcards') {
      navigate('/flashcards/Biologie'); // Default kategorie
    } else {
      // Kvíz - free uživatelé uvidí 10 otázek
      navigate(`/quiz/${category}`);
    }
  };

  const features = [
    {
      icon: Brain,
      title: 'Tisíce otázek',
      description: 'Obsáhlá databáze otázek z biologie, chemie, fyziky a somatologie připravená podle požadavků přijímacích zkoušek.',
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: BookOpen,
      title: 'Podrobná vysvětlení',
      description: 'Každá odpověď obsahuje detailní vysvětlení, abyste pochopili problematiku do hloubky.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Trophy,
      title: 'Simulace přijímaček',
      description: 'Realistické simulace přijímacích zkoušek s časovým limitem jako při skutečných testech.',
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: BarChart3,
      title: 'Sledování pokroku',
      description: 'Podrobné statistiky a analýzy vašeho učení, abyste věděli, kde se zlepšit.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Smartphone,
      title: 'Mobilní aplikace',
      description: 'Učte se kdykoliv a kdekoliv s nativními aplikacemi pro iOS a Android.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Clock,
      title: 'Flexibilní učení',
      description: 'Učte se svým vlastním tempem, kdykoliv máte čas. Vaše pokroky se synchronizují.',
      color: 'from-violet-500 to-purple-500'
    },
  ];

  const stats = [
    { value: '5 000+', label: 'Studentů' },
    { value: '95%', label: 'Úspěšnost' },
    { value: 'iOS & Android', label: 'Aplikace' },
  ];

  const subjects = [
    { name: 'Biologie', category: 'Biologie', questions: 200, color: 'from-green-500 to-emerald-600', customIcon: '/images/icons/bacteria.png' },
    { name: 'Chemie', category: 'Chemie', questions: 200, color: 'from-blue-500 to-indigo-600', customIcon: '/images/icons/chemistry.png' },
    { name: 'Fyzika', category: 'Fyzika', questions: 200, color: 'from-pink-500 to-rose-600', customIcon: '/images/icons/atom-2.png' },
    { name: 'Somatologie', category: 'Somatologie', questions: 40, color: 'from-teal-500 to-cyan-600', customIcon: '/images/icons/srdce.png' },
    { name: 'Simulace přijímaček', category: 'simulation', questions: null, color: 'from-amber-500 to-orange-600', description: 'Reálné testy', customIcon: '/images/icons/simulace.png' },
    { name: 'Flashcards', category: 'flashcards', questions: null, color: 'from-purple-500 to-violet-600', description: 'Kartičky k učení', customIcon: '/images/icons/flash1.png' },
  ];

  return (
    <div className="min-h-screen bg-dark-950">
      {/* Hero Section */}
      <section className="relative pt-24 pb-10 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-20 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 animate-slide-up">
              Chcete se dostat
              <span className="block gradient-text">na medicínu?</span>
            </h1>

            <p className="text-lg text-dark-400 max-w-2xl mx-auto mb-6 animate-slide-up">
              Vyzkoušej 10 otázek z každého předmětu zdarma a bez závazků.
            </p>

            <div className="animate-slide-up">
              <Link
                to="/register"
                className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl gradient-bg text-white font-semibold text-lg btn-hover"
              >
                <span>Vyzkoušet zdarma</span>
                <ArrowRight size={20} />
              </Link>
            </div>

          </div>

          {/* Stats */}
          <div className="mt-6 px-4">
            <div className="grid grid-cols-3 gap-2 sm:gap-6 max-w-md mx-auto">
              <div className="text-center">
                <div className="text-base sm:text-xl font-bold gradient-text">5 000+</div>
                <div className="text-dark-400 text-xs sm:text-sm">Studentů</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-xl font-bold gradient-text">95%</div>
                <div className="text-dark-400 text-xs sm:text-sm">Uživatelů zlepšilo své výsledky</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-xl font-bold gradient-text">iOS</div>
                <div className="text-dark-400 text-xs sm:text-sm">& Android</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Všechny předměty na jednom místě
            </h2>
            <p className="text-dark-400 max-w-2xl mx-auto">
              Komplexní pokrytí všech předmětů požadovaných na přijímacích zkouškách.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {subjects.map((subject, index) => {
              const isLocked = user && !hasActiveSubscription && (subject.category === 'simulation' || subject.category === 'flashcards');
              const isFreeQuiz = user && !hasActiveSubscription && subject.questions;

              return (
                <button
                  key={index}
                  onClick={() => subject.category && handleSubjectClick(subject.category)}
                  className={`glass rounded-2xl p-6 card-hover text-left transition-all ${
                    subject.category ? 'cursor-pointer hover:ring-2 hover:ring-primary-500/50' : 'opacity-60 cursor-not-allowed'
                  }`}
                  disabled={!subject.category}
                >
                  <div className="w-16 h-16 mb-4 relative flex items-center justify-center">
                    {subject.customIcon ? (
                      <img src={subject.customIcon} alt={subject.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${subject.color} flex items-center justify-center`}>
                        <subject.icon size={24} className="text-white" />
                      </div>
                    )}
                    {isLocked && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-dark-800 flex items-center justify-center">
                        <Lock size={10} className="text-amber-400" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{subject.name}</h3>
                  <p className="text-dark-400 text-sm">
                    {subject.questions ? `${subject.questions}+ otázek` : subject.description}
                  </p>
                  {/* Info pro free uživatele */}
                  {isFreeQuiz && (
                    <p className="text-amber-400 text-xs mt-1">
                      10 otázek zdarma
                    </p>
                  )}
                  {isLocked && (
                    <p className="text-amber-400 text-xs mt-1">
                      Pouze s předplatným
                    </p>
                  )}
                  {subject.category && (
                    <div className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-xl glass text-white text-sm font-medium hover:bg-dark-700 transition-colors">
                      {!user && subject.questions ? 'Registrovat se' : !user ? 'Pouze s předplatným' : isLocked ? 'Pouze s předplatným' : (subject.category === 'simulation' || subject.category === 'flashcards') ? 'Spustit' : 'Spustit test'}
                    </div>
                  )}
                  {!subject.category && (
                    <div className="mt-4 text-dark-500 text-sm">
                      Již brzy
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works - Video */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Jak to funguje
            </h2>
            <p className="text-dark-400 max-w-2xl mx-auto">
              Podívej se, jak snadná je příprava na přijímačky se Synapso.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-dark-700 shadow-2xl">
            <video
              className="w-full"
              controls
              playsInline
              preload="metadata"
              poster=""
            >
              <source src="/videos/synapso-demo.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 animated-gradient opacity-90"></div>
            <div className="relative px-8 py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Začni se připravovat ještě dnes
              </h2>
              <p className="text-white/80 max-w-xl mx-auto mb-8">
                Připojte se k tisícům studentů, kteří se s námi úspěšně připravili na přijímačky.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl bg-white text-dark-900 font-semibold text-lg hover:bg-dark-100 transition-colors"
              >
                <span>Vyzkoušet zdarma</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA - mobile only, non-logged-in users */}
      {!user && showStickyCta && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden">
          <div className="glass border-t border-dark-700 px-4 py-3">
            <Link
              to="/register"
              className="flex items-center justify-center space-x-2 w-full py-3 rounded-xl gradient-bg text-white font-semibold btn-hover"
            >
              <span>Vyzkoušet zdarma</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
